import { API_ORIGIN } from '../config'
const API_BASE = `${API_ORIGIN}/api`

export async function login(email, password) {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'Error al iniciar sesión')
  }
  return response.json()
}

export async function logout() {
  const response = await fetch(`${API_BASE}/auth/logout`, { method: 'POST' })
  if (!response.ok) throw new Error('Error al cerrar sesión')
  return response.json()
}

// Devuelve el usuario actual, o null si no hay sesión iniciada (401), en vez de lanzar error.
export async function fetchMe() {
  const response = await fetch(`${API_BASE}/auth/me`)
  if (response.status === 401) return null
  if (!response.ok) throw new Error('Error al obtener la sesión actual')
  return response.json()
}

// Termina "Ver como este parque": cierra la sesión de impersonación y quita la sesión del parque.
export async function exitImpersonation() {
  const response = await fetch(`${API_BASE}/auth/impersonation/exit`, { method: 'POST' })
  if (!response.ok) throw new Error('Error al salir de la impersonación')
  return response.json()
}

export async function getInvitationInfo(token) {
  const response = await fetch(`${API_BASE}/auth/invitations/${token}`)
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'Error al obtener la invitación')
  }
  return response.json()
}

// Registro autónomo: crea el parque en prueba y manda el correo de confirmación. Siempre responde igual.
export async function signup(payload) {
  const response = await fetch(`${API_BASE}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'SIGNUP_FAILED')
  }
  return response.json()
}

export async function activateAccount(payload) {
  const response = await fetch(`${API_BASE}/auth/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'Error al activar la cuenta')
  }
  return response.json()
}

export async function getLoginHistory() {
  const response = await fetch(`${API_BASE}/auth/login-history`)
  if (!response.ok) throw new Error('Error al obtener el historial de accesos')
  return response.json()
}

export async function logoutAllSessions() {
  const response = await fetch(`${API_BASE}/auth/logout-all`, { method: 'POST' })
  if (!response.ok) throw new Error('Error al cerrar las sesiones')
  return response.json()
}

export async function updateProfile(payload) {
  const response = await fetch(`${API_BASE}/auth/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'Error al guardar los datos personales')
  }
  return response.json()
}

export async function changePassword(currentPassword, newPassword) {
  const response = await fetch(`${API_BASE}/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'Error al cambiar la contraseña')
  }
  return response.json()
}

export const UPLOADS_BASE = `${API_ORIGIN}/uploads`
export const avatarSrc = (avatarUrl) => (avatarUrl ? `${UPLOADS_BASE}/${avatarUrl}` : '')

async function jsonOrThrow(response, fallback) {
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || fallback)
  }
  return response.json()
}

export async function uploadAvatar(file) {
  const formData = new FormData()
  formData.append('file', file)
  const response = await fetch(`${API_BASE}/auth/avatar`, { method: 'POST', body: formData })
  return jsonOrThrow(response, 'Error al subir la foto')
}

export async function deleteAvatar() {
  const response = await fetch(`${API_BASE}/auth/avatar`, { method: 'DELETE' })
  return jsonOrThrow(response, 'Error al quitar la foto')
}

export async function getNotificationPreferences() {
  const response = await fetch(`${API_BASE}/auth/notification-preferences`)
  return jsonOrThrow(response, 'Error al obtener las preferencias')
}

export async function saveNotificationPreferences(payload) {
  const response = await fetch(`${API_BASE}/auth/notification-preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return jsonOrThrow(response, 'Error al guardar las preferencias')
}

export async function saveTechnicianWork(payload) {
  const response = await fetch(`${API_BASE}/auth/technician-work`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return jsonOrThrow(response, 'Error al guardar los datos de trabajo')
}

export async function requestPasswordReset(email) {
  const response = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!response.ok) throw new Error('No se pudo enviar la solicitud. Intenta de nuevo')
  return response.json()
}

export async function getPasswordResetInfo(token) {
  const response = await fetch(`${API_BASE}/auth/reset-password/${encodeURIComponent(token)}`)
  if (!response.ok) throw new Error('No se pudo verificar el enlace')
  return response.json()
}

export async function resetPassword(token, password) {
  const response = await fetch(`${API_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || 'No se pudo cambiar la contraseña')
  }
  return response.json()
}

// Marca que el usuario ya vio (o se saltó) la guía de bienvenida. Si falla no pasa nada: solo volvería a ofrecerse.
export async function markTourSeen() {
  try {
    await fetch(`${API_BASE}/auth/tour-seen`, { method: 'POST' })
  } catch {
    // Silencioso: la guía es opcional.
  }
}